import { useState } from 'react';
import { Badge } from '../../../components/Badge';
import { Popover, PopoverTrigger, PopoverContent } from '../../../components/Popover';
import { Shield } from 'lucide-react';

interface RoleItem {
  id: string;
  name: string;
  code?: string;
  category?: string;
}

interface RoleChipsCellProps {
  roles?: RoleItem[];
}

const getServiceBadgeClass = (category?: string) => {
  const cat = (category || '').toLowerCase();
  if (cat.includes('support')) return 'bg-info/10 text-info border-info/30';
  if (cat.includes('asset')) return 'bg-primary/10 text-primary border-primary/30';
  if (cat.includes('admin')) return 'bg-foreground/10 text-foreground border-foreground/30';
  if (cat.includes('enduser')) return 'bg-success/10 text-success border-success/30';
  return 'bg-secondary text-muted-foreground border-border';
};

export function RoleChipsCell({ roles = [] }: RoleChipsCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const safeRoles = Array.isArray(roles) ? roles.filter(Boolean) : [];

  if (safeRoles.length === 0) {
    return <span className="text-[11px] text-muted-foreground italic">No roles assigned</span>;
  }

  const visibleRoles = safeRoles.slice(0, 2);
  const overflowRoles = safeRoles.slice(2);
  const overflowCount = overflowRoles.length;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {visibleRoles.map((role, idx) => (
        <Badge
          key={role.id || role.code || `role-${idx}`}
          variant="outline"
          className={`text-[10px] py-0.5 px-2 border truncate max-w-[140px] font-normal ${getServiceBadgeClass(role.category)}`}
          title={role.name || role.code}
        >
          {role.name || role.code || 'Role'}
        </Badge>
      ))}

      {overflowCount > 0 && (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-0.5 rounded-full bg-secondary hover:bg-secondary/80 border border-border px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              title="Click to view all assigned roles"
            >
              +{overflowCount}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-3 shadow-lg bg-card border border-border">
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 pb-2 border-b border-border text-xs font-semibold text-foreground">
                <Shield className="size-3.5 text-primary" />
                <span>All Assigned Roles ({safeRoles.length})</span>
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto pt-1">
                {safeRoles.map((role, idx) => (
                  <Badge
                    key={role.id || role.code || `all-role-${idx}`}
                    variant="outline"
                    className={`text-[10px] py-0.5 px-2 border font-normal ${getServiceBadgeClass(role.category)}`}
                  >
                    {role.name || role.code || 'Role'}
                  </Badge>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
